import React, { useState } from 'react';
import { Send, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { WHATSAPP_NUMBER } from '../../lib/seoConfig';

export const ProductEnquiryForm = ({ productTitle }) => {
  const [formData, setFormData] = useState({ name: '', phone: '', city: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/submit-quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, projectType: productTitle })
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to submit enquiry');
      }
      const result = await response.json();
      toast.success(result.message);
      setFormData({ name: '', phone: '', city: '', message: '' });
    } catch (error) {
      toast.error(error.message || 'Unable to submit request. Please call +91-9351292512');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="product-enquiry-form" className="py-16 bg-gradient-to-br from-alden-green to-alden-green-light" data-testid="product-enquiry-section">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">Request Project Quotation</h2>
            <p className="text-white/90 text-lg">
              Get a detailed quotation for the {productTitle} within 24 hours — no obligation.
            </p>
          </div>
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-2xl p-6 md:p-8 space-y-4" data-testid="product-enquiry-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <Input
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                placeholder="Your Name *"
                className="h-12 text-base"
                data-testid="enquiry-name-input"
              />
              <Input
                name="phone"
                type="tel"
                required
                pattern="[0-9+\-\s]{10,15}"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Phone / WhatsApp Number *"
                className="h-12 text-base"
                data-testid="enquiry-phone-input"
              />
            </div>
            <Input
              name="city"
              value={formData.city}
              onChange={handleChange}
              placeholder="City / Plant Location"
              className="h-12 text-base"
              data-testid="enquiry-city-input"
            />
            <Textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Tell us about your project requirements (optional)"
              rows={3}
              className="text-base"
              data-testid="enquiry-message-input"
            />
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-alden-green hover:bg-alden-green-light text-white h-14 text-lg font-semibold"
                data-testid="enquiry-submit-button"
              >
                <Send className="mr-2 w-5 h-5" />
                {isSubmitting ? 'Submitting...' : 'Request Quotation'}
              </Button>
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`Hi, I need a quotation for ${productTitle}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1"
                data-testid="enquiry-whatsapp-link"
              >
                <Button type="button" className="w-full bg-[#25D366] hover:bg-[#1ebe5b] text-white h-14 text-lg font-semibold">
                  <MessageCircle className="mr-2 w-5 h-5" />
                  WhatsApp Us
                </Button>
              </a>
            </div>
            <p className="text-xs text-gray-500 text-center">
              Your details are safe with us. Our engineers respond within 24 working hours.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ProductEnquiryForm;
